import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../api/api';
import './LogoManager.css';

const LogoManager = ({ onLogoSelect, selectedLogo, onClose }) => {
  const [logos, setLogos] = useState([]);
  const [defaultLogo, setDefaultLogo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [toast, setToast] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadLogos();
    loadDefaultLogo();
  }, []);

  const loadLogos = async () => {
    try {
      const response = await apiFetch('/api/logo/logos');
      setLogos(response);
    } catch (error) {
      console.error('Failed to load logos:', error);
      showToast('❌ Failed to load logos');
    }
  };

  const loadDefaultLogo = async () => {
    try {
      const response = await apiFetch('/api/logo/default');
      setDefaultLogo(response.defaultLogo);
    } catch (error) {
      console.error('Failed to load default logo:', error);
    }
  };

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(''), 3000);
  };

  const handleFileSelect = (files) => {
    if (files && files.length > 0) {
      uploadLogo(files[0]);
    }
  };

  const uploadLogo = async (file) => {
    if (!file.type.startsWith('image/')) {
      showToast('❌ Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast('❌ File size must be less than 5MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('logo', file);

    try {
      const response = await fetch('/api/logo/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      showToast('✅ Logo uploaded successfully!');
      await loadLogos();
    } catch (error) {
      console.error('Upload failed:', error);
      showToast('❌ Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const deleteLogo = async (filename) => {
    if (!window.confirm('Are you sure you want to delete this logo?')) return;

    try {
      await apiFetch(`/api/logo/logos/${filename}`, { method: 'DELETE' });
      showToast('✅ Logo deleted successfully');
      await loadLogos();
      
      // If deleted logo was selected, clear selection
      if (selectedLogo === filename) {
        onLogoSelect(null);
      }
    } catch (error) {
      console.error('Failed to delete logo:', error);
      showToast('❌ Failed to delete logo');
    }
  };

  const setAsDefault = async (filename) => {
    try {
      await apiFetch(`/api/logo/set-default/${filename}`, { method: 'POST' });
      setDefaultLogo(filename);
      showToast('✅ Default logo updated');
    } catch (error) {
      console.error('Failed to set default logo:', error);
      showToast('❌ Failed to set default logo');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileSelect(files);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="logo-manager">
      <div className="logo-manager-header">
        <h3>Logo Manager</h3>
        <button className="close-btn" onClick={onClose}>✕</button>
      </div>

      <div className="logo-manager-content">
        {/* Upload Area */}
        <div 
          className={`upload-area ${dragOver ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => handleFileSelect(e.target.files)}
          />
          
          {uploading ? (
            <div className="upload-spinner">
              <div className="spinner"></div>
              <p>Uploading...</p>
            </div>
          ) : (
            <>
              <div className="upload-icon">📤</div>
              <p>Drop your logo here or click to browse</p>
              <small>Supports: JPG, PNG, GIF, SVG (Max 5MB)</small>
            </>
          )}
        </div>

        {/* Logo Grid */}
        <div className="logos-grid">
          {logos.length === 0 ? (
            <div className="empty-state">
              <p>No logos uploaded yet</p>
              <small>Upload your first logo using the area above</small>
            </div>
          ) : (
            logos.map((logo) => (
              <div 
                key={logo.filename} 
                className={`logo-item ${selectedLogo === logo.filename ? 'selected' : ''}`}
              >
                <div className="logo-preview">
                  <img 
                    src={logo.url} 
                    alt={logo.originalName}
                    onError={(e) => {
                      e.target.src = '/placeholder-logo.png';
                    }}
                  />
                  {defaultLogo === logo.filename && (
                    <div className="default-badge">Default</div>
                  )}
                </div>
                
                <div className="logo-info">
                  <div className="logo-name">{logo.originalName}</div>
                  <div className="logo-size">{formatFileSize(logo.size)}</div>
                </div>

                <div className="logo-actions">
                  <button
                    className={`select-btn ${selectedLogo === logo.filename ? 'selected' : ''}`}
                    onClick={() => onLogoSelect(logo.filename)}
                  >
                    {selectedLogo === logo.filename ? '✓ Selected' : 'Select'}
                  </button>
                  
                  {defaultLogo !== logo.filename && (
                    <button
                      className="default-btn"
                      onClick={() => setAsDefault(logo.filename)}
                      title="Set as default logo"
                    >
                      ⭐ Set Default
                    </button>
                  )}
                  
                  <button
                    className="delete-btn"
                    onClick={() => deleteLogo(logo.filename)}
                    title="Delete logo"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Usage Instructions */}
        <div className="usage-instructions">
          <h4>How to use logos in your header:</h4>
          <ol>
            <li>Upload or select a logo above</li>
            <li>In the header editor, use the <strong>Logo</strong> button to insert: <code>{'{{logo}}'}</code></li>
            <li>Or manually type: <code>{'{{logo:filename}}'}</code> for a specific logo</li>
            <li>The logo will automatically appear in your reports</li>
          </ol>
          <div className="template-examples">
            <p><strong>Template variables:</strong></p>
            <ul>
              <li><code>{'{{logo}}'}</code> - Uses the default logo</li>
              <li><code>{'{{logo:' + (selectedLogo || 'filename') + '}}'}</code> - Uses a specific logo</li>
            </ul>
          </div>
        </div>
      </div>

      {toast && (
        <div className="toast-notification">
          {toast}
        </div>
      )}
    </div>
  );
};

export default LogoManager; 